import { useState } from 'react'
import { Box, Button, Flex, Skeleton, Stack, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { TbUsers } from 'react-icons/tb'
import { Child, deleteChild, getChildren, patchChild, postChild } from '../../api/user/family/Children'
import type { ChildFormValues } from '../../components/family/ChildForm'
import ChildForm from '../../components/family/ChildForm'
import ChildRow from '../../components/family/ChildRow'
import ArchiveChildDialog from '../../components/family/ArchiveChildDialog'
import BlankState from '../../components/input/BlankState'
import handleToast from '../../components/toast/handleToast'

interface FamilyPageProps {
  isMobile?: boolean
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

// A Child is a structural superset of the two ChildForm contract fields, so it
// is handed straight to ChildForm for edit prefill. ChildForm reads only the two
// fields it owns; the birth date is rendered solely inside ChildForm's native
// date input and never on this list module (FAM-06). Typed as a Child → form
// prefill so this module never names the birth-date field as a string literal.
const toFormValues = (child: Child): ChildFormValues => ({
  display_name: child.display_name,
  date_of_birth: child.date_of_birth,
})

// "My Family" settings screen. Lists only the parent's active children by name
// (the birth date never renders on the list — it lives solely inside ChildForm,
// D-10/FAM-06). Add and edit use the same inline-expanding ChildForm (D-03);
// archive is confirm-gated via ArchiveChildDialog (D-06). Every add/edit/archive
// invalidates the ['children'] cache so the list is never stale (D-11).
const FamilyPage = ({ isMobile }: FamilyPageProps) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  const childrenQuery = useQuery(['children'], getChildren)
  const children: Child[] = (childrenQuery.data ?? []).filter((c: Child) => c.archived_at === null)

  const [addOpen, setAddOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<Child | null>(null)
  const archiveDisclosure = useDisclosure()

  const addMutation = useMutation((values: ChildFormValues) => postChild(values), {
    onSuccess: (_data, values) => {
      handleToast(toast, `${values.display_name} added`)
      queryClient.invalidateQueries(['children'])
      setAddOpen(false)
    },
    onError: () => handleToast(toast, GENERIC_ERROR),
  })

  const editMutation = useMutation(
    ({ id, values }: { id: string; values: ChildFormValues }) => patchChild({ id, data: values }),
    {
      onSuccess: () => {
        handleToast(toast, 'Changes saved')
        queryClient.invalidateQueries(['children'])
        setEditingId(null)
      },
      onError: () => handleToast(toast, GENERIC_ERROR),
    }
  )

  const archiveMutation = useMutation((child: Child) => deleteChild({ id: child.id }), {
    onSuccess: (_data, child) => {
      handleToast(toast, `${child.display_name} archived`)
      queryClient.invalidateQueries(['children'])
      archiveDisclosure.onClose()
      setArchiveTarget(null)
    },
    onError: () => {
      handleToast(toast, GENERIC_ERROR)
      // Close the dialog on failure so the user cannot re-fire the failed request.
      archiveDisclosure.onClose()
      setArchiveTarget(null)
    },
  })

  const openArchive = (child: Child) => {
    setArchiveTarget(child)
    archiveDisclosure.onOpen()
  }

  const confirmArchive = () => {
    if (archiveTarget) archiveMutation.mutate(archiveTarget)
  }

  const startEdit = (id: string) => {
    setAddOpen(false)
    setEditingId(id)
  }

  return (
    <Box w="100%" p={isMobile ? '0px' : '30px 0px'}>
      <Text variant="title">My Family</Text>
      <Text variant="subtle" mt="6px">
        Manage the kids on your account. They'll be selectable when you book events.
      </Text>

      <Button
        variant="heavy"
        minH={11}
        mt="20px"
        onClick={() => {
          setEditingId(null)
          setAddOpen((prev) => !prev)
        }}
      >
        Add child
      </Button>

      {addOpen && (
        <Box mt="16px" p="16px" borderRadius="8px" bg="#F5F7F9">
          <ChildForm
            onSubmit={(values) => addMutation.mutate(values)}
            isSubmitting={addMutation.isLoading}
            submitLabel="Save child"
          />
        </Box>
      )}

      <Box mt="24px">
        {childrenQuery.isLoading ? (
          <Stack spacing={3}>
            <Skeleton height="44px" borderRadius="6px" />
            <Skeleton height="44px" borderRadius="6px" />
            <Skeleton height="44px" borderRadius="6px" />
          </Stack>
        ) : children.length === 0 ? (
          <BlankState
            icon={<TbUsers size={48} />}
            title="No kids yet"
            description="Add a child to book events for them. You can edit or archive them anytime."
          />
        ) : (
          <Stack spacing={2} divider={<Box borderBottom="1px solid #DCE2EA" />}>
            {children.map((child) =>
              editingId === child.id ? (
                <Box key={child.id} p="16px" borderRadius="8px" bg="#F5F7F9">
                  <ChildForm
                    initialValues={toFormValues(child)}
                    onSubmit={(values) => editMutation.mutate({ id: child.id, values })}
                    isSubmitting={editMutation.isLoading}
                    submitLabel="Save changes"
                  />
                  <Button variant="link" mt="8px" onClick={() => setEditingId(null)}>
                    Discard
                  </Button>
                </Box>
              ) : (
                <Flex key={child.id} w="100%">
                  <ChildRow child={child} onEdit={startEdit} onArchive={openArchive} />
                </Flex>
              )
            )}
          </Stack>
        )}
      </Box>

      <ArchiveChildDialog
        isOpen={archiveDisclosure.isOpen}
        onClose={() => {
          archiveDisclosure.onClose()
          setArchiveTarget(null)
        }}
        onConfirm={confirmArchive}
        isLoading={archiveMutation.isLoading}
        name={archiveTarget?.display_name ?? ''}
      />
    </Box>
  )
}

export default FamilyPage
